import { usePopupRef } from '@framework/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faFolderOpen, faFolderTree, faPen } from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';

type Props = {
  onEdit(): void;
  onDelete(): void;
  showDuplicateMenu: boolean;
  onDuplicateDirectory(): void;
  onMoveFolder(): void;
  onMoveToOtherWorkspace(): void;
};

export const MenuButtons: React.FC<Props> = ({
  onDelete,
  onEdit,
  onDuplicateDirectory,
  showDuplicateMenu,
  onMoveFolder,
  onMoveToOtherWorkspace,
}: Props) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleClose, handleOpen } = usePopupRef<HTMLDivElement>(false);

  const handleDelete = () => {
    onDelete();
    handleClose();
  };

  const handleMoveFolder = () => {
    onMoveFolder();
    handleClose();
  };

  const handleDuplicateDirectory = () => {
    onDuplicateDirectory();
    handleClose();
  };

  const handleMoveToOtherWorkspace = () => {
    onMoveToOtherWorkspace();
    handleClose();
  };

  return (
    <div className="relative">
      <div className="inline-flex text-sm">
        <button
          className="whitespace-nowrap rounded-l bg-gray-500 py-1 pl-3 pr-2 font-bold text-white hover:bg-gray-700"
          onClick={onEdit}
        >
          <FontAwesomeIcon icon={faPen} className="mr-1" />
          {t('common.action.edit')}
        </button>
        <button className="rounded-r bg-gray-500 px-2 py-1 font-bold text-white hover:bg-gray-700" onClick={handleOpen}>
          <FontAwesomeIcon icon={faEllipsisV} />
        </button>
      </div>

      <div
        className={'absolute z-10 w-56 rounded-lg bg-white py-1 shadow-lg ' + (isOpen ? 'block' : 'hidden')}
        ref={popupRef}
      >
        <button
          className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
          onClick={handleMoveFolder}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faFolderOpen} />
          </span>
          <span className="text-sm">{t('workspace.folder.move.label')}</span>
        </button>

        <button
          className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
          onClick={handleMoveToOtherWorkspace}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faFolderTree} />
          </span>
          <span className="text-sm">{t('workspace.moveToAnother.label')}</span>
        </button>

        {showDuplicateMenu && (
          <button
            className="block w-full px-2 py-1 text-left text-gray-800 hover:bg-indigo-500 hover:text-white"
            onClick={handleDuplicateDirectory}
          >
            <span className="inline-block w-6 text-center">
              <FontAwesomeIcon icon={faCopy} />
            </span>
            <span className="text-sm">{t('workspace.folder.duplicate.label')}</span>
          </button>
        )}

        <button
          className="block w-full px-2 py-1 text-left text-red-700 hover:bg-indigo-500 hover:text-white"
          onClick={handleDelete}
        >
          <span className="inline-block w-6 text-center">
            <FontAwesomeIcon icon={faTrashAlt} />
          </span>
          <span className="text-sm">{t('common.action.delete')}</span>
        </button>
      </div>
    </div>
  );
};
